import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";

const DRIVER_CATEGORIES = new Set([
  "company driver", "agency driver", "owner operator", "owner operator driver",
  "o/o", "o/o's driver", "o/o driver",
]);
const RECORD_TYPES = [
  { key: "incidents", label: "Incidents", icon: "fa-car-crash", taskKeys: ["incident", "incidents", "incident_accident"] },
  { key: "violations", label: "Violations", icon: "fa-exclamation-triangle", taskKeys: ["violation", "violations"] },
];

function DetailRow({ label, icon, children }) {
  return <div className="profile-row">
    <span className="profile-icon" aria-hidden="true"><i className={`fas ${icon}`} /></span>
    <div className="profile-text">
      <span className="profile-field-label">{label}:</span>
      <span>{children}</span>
    </div>
  </div>;
}

export default function ProfileDetails({ userId, createdDate, categoryName, categoryLabel, categoryReady = true }) {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const isDriver = [categoryName, categoryLabel].some(name => DRIVER_CATEGORIES.has(String(name || "").trim().toLowerCase().replace(/\u2019/g, "'")));
  const [summary, setSummary] = useState(null);
  const requestKey = `${userId}:${categoryName || ""}:${categoryLabel || ""}`;

  useEffect(() => {
    let active = true;
    setSummary(null);
    if (!categoryReady || !userId || !isDriver) return () => { active = false; };
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
    (async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/tasks/assigned/${userId}`, config);
        if (!active) return;
        const tasks = response.data?.tasks || [];
        const types = RECORD_TYPES.filter(type => tasks.some(task =>
          type.taskKeys.includes(String(task.t_key || "").toLowerCase()) ||
          String(task.t_route || "").toLowerCase().startsWith(`/${type.key}/`)));
        const counts = await Promise.all(types.map(async type => {
          let count;
          try {
            const records = await axios.get(`${baseUrl}/api/${type.key}/${userId}`, config);
            count = Array.isArray(records.data) ? records.data.length : "Unavailable";
          } catch {
            count = "Unavailable";
          }
          return [type.key, count];
        }));
        if (active) setSummary({ requestKey, counts: Object.fromEntries(counts) });
      } catch {
        if (active) setSummary({ requestKey, error: true });
      }
    })();
    return () => { active = false; };
  }, [baseUrl, userId, isDriver, categoryReady, requestKey]);

  const current = summary?.requestKey === requestKey ? summary : null;
  const loading = !categoryReady || (isDriver && userId && !current);
  const date = createdDate ? dayjs(createdDate) : null;
  return <section className="profile-details" aria-label="Details">
    <div className="card-title">Details</div>
    <div className="card-content profile-table">
      {loading ? <p className="profile-empty-value" role="status">Loading details...</p> : <>
      <DetailRow label="Applied" icon="fa-calendar-alt">{date?.isValid() ? date.format("MM/DD/YYYY") : "Not provided"}</DetailRow>
      {isDriver && RECORD_TYPES.filter(type => Object.prototype.hasOwnProperty.call(current?.counts || {}, type.key)).map(type =>
        <DetailRow key={type.key} label={type.label} icon={type.icon}>{current.counts[type.key]}</DetailRow>)}
      {isDriver && current?.error && <p className="profile-empty-value" role="status">Record counts unavailable</p>}
      </>}
    </div>
  </section>;
}
